import { gql, NetworkStatus, TypedDocumentNode } from '@apollo/client'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Button } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { FeatureFlag } from '@repo/shared-common'
import { useNavigate, useSearch } from '@tanstack/react-router'
import LogoSymbol from '@web/src/components/primatives/logo-symbol/logo-symbol'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { usePwa } from '@web/src/contexts/pwa-context'
import HideProfileFromFeedDialog from '@web/src/features/feed-hidden/hide-profile-from-feed-dialog/hide-profile-from-feed-dialog'
import LikesViewDialog from '@web/src/features/likes/likes-view-dialog/likes-view-dialog'
import PostCard, { APPROVE_POST_MUTATION, PostProps } from '@web/src/features/post/post-card/post-card'
import PostCreateEditDialog from '@web/src/features/post/post-create-edit-dialog/post-create-edit-dialog'
import PostDeleteDialog from '@web/src/features/post/post-delete-dialog/post-delete-dialog'
import PostMenu from '@web/src/features/post/post-menu/post-menu'
import PostViewDialog from '@web/src/features/post/post-view-dialog/post-view-dialog'
import PostWhatsNewCard from '@web/src/features/post/post-whats-new-card/post-whats-new-card'
import RendezvousPreview from '@web/src/features/rendezvous/rendezvous-preview/rendezvous-preview'
import ReportCreate from '@web/src/features/report/report-create/report-create'
import { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { MediaInfo, PostInfo, ProfileBasicInfo, ProfileMediaInfo } from 'src/lib/fragments'
import { useTranslation } from 'src/lib/intl/react'
import type {
  PostApprovalStatus,
  PostFeedQuery,
  PostFeedQueryVariables,
  PostFilterType,
  PostSortEnum,
} from 'types/graphql'
import { useAuth } from '../../../hooks/use-auth'
import ImmersiveMediaDialog from '../../media/immersive-media-dialog/immersive-media-dialog'

const POSTS_QUERY: TypedDocumentNode<PostFeedQuery, PostFeedQueryVariables> = gql`
  query PostFeedQuery($page: Int!, $filter: PostFilter, $sortBy: PostSortEnum) {
    postsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        ...PostInfo
        mediaToPost {
          media {
            ...MediaInfo
          }
        }
        owner {
          ...ProfileBasicInfo
        }
        trendScore
      }
      page
      more
    }
  }
  ${PostInfo}
  ${MediaInfo}
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

type PostsComponentProps = {
  filterType: PostFilterType
  profileId?: string
  groupId?: string
  eventId?: string
  approvalFilter?: PostApprovalStatus | ''
  canApprove?: boolean
  showSort?: boolean
  showApprovalFilter?: boolean
  noSideBars?: boolean
  showWhatsNew?: boolean
  showRendezvousPreview?: boolean
  disableInteraction?: boolean
  disableCreatePost?: boolean
  disablePoints?: boolean
  onPostChange?: () => void
}

type PostsFilterFormValues = {
  sortBy?: PostSortEnum
  approvalFilter?: PostApprovalStatus
}

const Posts = ({
  filterType,
  profileId,
  groupId,
  eventId,
  approvalFilter,
  canApprove = false,
  showSort = true,
  showApprovalFilter = false,
  noSideBars = false,
  showWhatsNew = true,
  showRendezvousPreview = false,
  disableInteraction = false,
  disableCreatePost = false,
  disablePoints = false,
  onPostChange,
}: PostsComponentProps) => {
  const { t } = useTranslation()
  const params = useSearch({ strict: false })
  const navigate = useNavigate()
  const { getFeature, currentUser } = useAuth()
  const { onDisplayStateChange } = usePwa()
  const [lastQueryTime, setLastQueryTime] = useState<number>(0)
  const { cache } = useApolloClient()
  const formMethods = useForm<PostsFilterFormValues>({
    defaultValues: {
      sortBy: 'LATEST' as PostSortEnum,
      approvalFilter: 'PENDING' as PostApprovalStatus,
    },
  })
  const [editModal, setEditModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [likesModal, setLikesModal] = useState(false)
  const [reportModal, setReportModal] = useState(false)
  const [hideFromFeedModal, setHideFromFeedModal] = useState(false)
  const [activePost, setActivePost] = useState<PostProps | null>(null)
  const [immersiveMediaOpen, setImmersiveMediaOpen] = useState(false)
  const [immersiveMediaId, setImmersiveMediaId] = useState<string>('')
  const [immersivePostId, setImmersivePostId] = useState<string>('')
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false)

  const [approvePost, { loading: approveLoading }] = useMutation(APPROVE_POST_MUTATION, {
    onError: (error) => {
      console.error('Error approving/rejecting post:', error)
    },
  })

  const searchSortBy: PostSortEnum = params.sortBy ? (params.sortBy as PostSortEnum) : 'LATEST'
  const searchApprovalFilter: PostApprovalStatus = params.approvalFilter
    ? (params.approvalFilter as PostApprovalStatus)
    : 'PENDING'

  useEffect(() => {
    formMethods.reset({
      sortBy: searchSortBy,
      approvalFilter: searchApprovalFilter,
    })
  }, [searchSortBy, searchApprovalFilter, formMethods])

  const approvalStatus = showApprovalFilter ? searchApprovalFilter : approvalFilter || undefined

  const buildFilter = (): PostFeedQueryVariables['filter'] => ({
    type: filterType,
    profileId: profileId ? profileId : undefined,
    groupId: groupId ? groupId : undefined,
    eventId: eventId ? eventId : undefined,
    approvalStatus: approvalStatus as PostApprovalStatus,
  })

  const queryResult = useQuery<PostFeedQuery, PostFeedQueryVariables>(POSTS_QUERY, {
    variables: {
      page: 1,
      filter: buildFilter(),
      sortBy: searchSortBy,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const { data, error, networkStatus } = queryResult

  // Initialize lastQueryTime on client mount to avoid hydration mismatch
  useEffect(() => {
    if (lastQueryTime === 0) {
      setLastQueryTime(Date.now())
    }
  }, [lastQueryTime])

  // Set up the display state change handler, this is used to refetch the posts if the user has
  // been away for more than 15 minutes and the app has been in the background
  useEffect(() => {
    if (onDisplayStateChange) {
      onDisplayStateChange((event) => {
        // Check if the display state changed to visible
        if (event.newState === 'visible') {
          const currentTime = Date.now()
          const timeSinceLastQuery = currentTime - lastQueryTime

          // If it's been more than 15 minutes since the last query, refetch
          if (timeSinceLastQuery > 15 * 60 * 1000) {
            queryResult.refetch()
            setLastQueryTime(currentTime)
          }
        }
      })
    }
  }, [onDisplayStateChange, queryResult, lastQueryTime])

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  const postsPage = data?.postsPage ?? null

  const handlePostPublished = (postData) => {
    // Inject the post at the top of the current data set
    const result = cache.readQuery({
      query: POSTS_QUERY,
      variables: queryResult.variables,
    })
    cache.writeQuery({
      query: POSTS_QUERY,
      data: {
        postsPage: {
          entries: [postData, ...(result?.postsPage?.entries ?? [])],
          // A negative page means the data has been updated via cache and we should not merge, the cache looks at this
          page: -1,
          more: result?.postsPage?.more ?? false,
        },
      },
      variables: queryResult.variables,
    })

    onPostChange?.()
  }

  let shouldShowWhatsNew = showWhatsNew

  if (profileId && profileId !== currentUser.profile.id) {
    shouldShowWhatsNew = false
  }

  const handleFetchMore = () => {
    if (!postsPage) return

    queryResult.fetchMore({
      variables: {
        page: postsPage.page + 1,
        filter: buildFilter(),
      },
    })
  }

  const filterResults = (fieldData: { sortBy?: PostSortEnum; approvalFilter?: PostApprovalStatus }) => {
    navigate({
      search: {
        sortBy: fieldData.sortBy,
        approvalFilter: fieldData.approvalFilter,
      },
      replace: true,
    } as never)
  }

  const handleViewPost = (post) => {
    setActivePost(post)
    setViewModal(true)
  }

  const handleDeletePost = (post) => {
    setActivePost(post)
    setDeleteModal(true)
  }

  const handleReportPost = (post) => {
    setActivePost(post)
    setReportModal(true)
  }

  const handleHideFromFeed = (post: PostProps) => {
    setActivePost(post)
    setHideFromFeedModal(true)
  }

  const handleEditPost = (post) => {
    setActivePost(post)
    setEditModal(true)
  }

  const handleOpenLikes = (post) => {
    setActivePost(post)
    setLikesModal(true)
  }

  const handleApprovePost = async (post: PostProps) => {
    try {
      await approvePost({
        variables: { postId: post.id, approve: true },
      })
    } catch {
      // Handled in onError
    }
  }

  const handleRejectPost = (post: PostProps) => {
    setActivePost(post)
    setRejectConfirmOpen(true)
  }

  const handleRejectConfirm = async () => {
    if (!activePost) return
    try {
      await approvePost({
        variables: { postId: activePost.id, approve: false },
      })
      setRejectConfirmOpen(false)
    } catch {
      // Handled in onError
    }
  }

  return (
    <>
      <div className={cn('mb-4 grid w-full grid-cols-2 gap-3 px-0', noSideBars ? 'md:grid-cols-2' : 'md:grid-cols-4')}>
        {!noSideBars && (
          <div className="col-span-2 mt-0 md:col-span-1 md:mt-4">
            <PostMenu className="w-full" />
          </div>
        )}
        <div className="col-span-2 mt-0 md:col-span-2 md:mt-4">
          <InfiniteScroll
            dataLength={(postsPage?.entries?.length ?? 0) + 1}
            hasMore={postsPage?.more ?? false}
            loader={
              <div className="pt-4">
                <SFPageLoading />
              </div>
            }
            next={handleFetchMore}
            style={{ overflow: 'hidden' }}
          >
            <div className="flex w-full flex-col gap-3 px-0.5">
              {shouldShowWhatsNew && (
                <PostWhatsNewCard
                  disableCreatePost={disableCreatePost}
                  disablePoints={disablePoints}
                  eventId={eventId}
                  groupId={groupId}
                  onPostPublished={handlePostPublished}
                  rightChild={
                    <Form className="contents" formMethods={formMethods} onSubmit={filterResults}>
                      <div className="flex flex-wrap items-end justify-end gap-3">
                        {showApprovalFilter && (
                          <div className="flex items-center gap-2">
                            <span className="shrink-0 text-nowrap">{t('Filter By:')}</span>
                            <NativeSelectFormField
                              fieldProps={{ className: 'min-w-[115px]' }}
                              name="approvalFilter"
                              onValueChange={() => {
                                formMethods
                                  .handleSubmit(filterResults)()
                                  .catch(() => undefined)
                              }}
                            >
                              <option value="PENDING">{t('Pending')}</option>
                              <option value="REJECTED">{t('Rejected')}</option>
                            </NativeSelectFormField>
                          </div>
                        )}
                        {showSort && (
                          <div className="flex min-w-[100px] items-center self-end sm:min-w-[115px]">
                            <NativeSelectFormField
                              name="sortBy"
                              onValueChange={(e) => {
                                if (e.target.value === 'FEED') {
                                  navigate({ to: '/feed', search: { sortBy: 'FEED' } } as never)
                                  formMethods.reset({
                                    sortBy: searchSortBy,
                                    approvalFilter: searchApprovalFilter,
                                  })
                                  return
                                }
                                formMethods
                                  .handleSubmit(filterResults)()
                                  .catch(() => undefined)
                              }}
                            >
                              <option value="LATEST">{t('Latest')}</option>
                              <option value="FEED">{t('New Feed')}</option>
                            </NativeSelectFormField>
                          </div>
                        )}
                      </div>
                    </Form>
                  }
                />
              )}

              {/* Show rendezvous preview */}
              {showRendezvousPreview && <RendezvousPreview />}

              {postsPage?.entries?.map((post) => {
                return (
                  <PostCard
                    canApprove={canApprove}
                    disableInteraction={disableInteraction}
                    key={post.id}
                    onMediaSelected={(mediaId, postId) => {
                      setImmersiveMediaId(mediaId)
                      setImmersivePostId(postId)
                      setImmersiveMediaOpen(true)
                    }}
                    performApprovePost={handleApprovePost}
                    performDeletePost={handleDeletePost}
                    performEditPost={handleEditPost}
                    performHideFromFeed={disableInteraction ? undefined : handleHideFromFeed}
                    performOpenLikes={handleOpenLikes}
                    performOpenPost={handleViewPost}
                    performRejectPost={handleRejectPost}
                    performReportPost={handleReportPost}
                    post={post}
                  />
                )
              })}
              {postsPage?.entries?.length === 0 && (
                <div className="flex flex-col items-center gap-2">
                  <p className="font-bold">{t('No posts')}</p>
                </div>
              )}
              {!postsPage && <SFPageLoading />}
            </div>

            {postsPage && postsPage.page >= getFeature(FeatureFlag.POST_MAX_PAGES) && !groupId && !eventId && (
              <div className="flex flex-col items-center gap-6 pt-6">
                <p>{t('Become a supporter to see more...')}</p>
                <Button onClick={() => navigate({ to: '/supporter' })} type="button">
                  <LogoSymbol imageClassName="w-5 drop-shadow(1px 2px 1px #000000)" />
                  <span className="font-semibold">{t('Support Spicy Friends')}</span>
                </Button>
              </div>
            )}
          </InfiniteScroll>
        </div>
        {!noSideBars && <div className="col-span-1 mt-0 hidden md:block" />}
      </div>

      <PostCreateEditDialog
        eventId={eventId}
        groupId={groupId}
        onOpenChange={(e) => setEditModal(e.open)}
        open={editModal}
        post={activePost}
      />

      <PostDeleteDialog onOpenChange={(e) => setDeleteModal(e.open)} open={deleteModal} post={activePost} />

      <ReportCreate
        onOpenChange={(e) => setReportModal(e.open)}
        open={reportModal}
        targetId={activePost?.id}
        targetKind="POST"
      />

      <HideProfileFromFeedDialog
        onOpenChange={(e) => setHideFromFeedModal(e.open)}
        open={hideFromFeedModal}
        profile={{ id: activePost?.owner?.id, name: activePost?.owner?.name }}
      />

      <PostViewDialog onOpenChange={(e) => setViewModal(e.open)} open={viewModal} postId={activePost?.id} />

      <LikesViewDialog onOpenChange={(e) => setLikesModal(e.open)} open={likesModal} postId={activePost?.id} />

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Reject')}
        loading={approveLoading}
        onConfirm={handleRejectConfirm}
        onOpenChange={(e) => setRejectConfirmOpen(e.open)}
        open={rejectConfirmOpen}
        text={t('Are you sure you want to reject this post?')}
        title={t('Reject Post')}
        variant="basic"
      />

      <ImmersiveMediaDialog
        filter={{ postId: immersivePostId }}
        mediaId={immersiveMediaId}
        onOpenChange={() => setImmersiveMediaOpen(false)}
        open={immersiveMediaOpen}
      />
    </>
  )
}

export default Posts
